class CollectionManager {
  constructor() {
    this._grid       = document.querySelector('[data-collection-grid]');
    if (!this._grid) return;

    this._pagination = document.querySelector('[data-collection-pagination]');
    this._isLoading  = false;
    this._firstScroll = false;
    this._filterRequestController = null;

    this._onScroll       = this._onScroll.bind(this);
    this._onFilterClick  = this._onFilterClick.bind(this);
    this._onFilterChange = this._onFilterChange.bind(this);

    this._init();
  }

  _init() {
    this._initGridSwitcher();
    this._initFilters();

    if (this._grid.dataset.infiniteScroll === 'true') {
      this._initInfiniteScroll();
    }

    this._updateCount();
  }

  _initGridSwitcher() {
    const toggleDesktop = document.querySelector('[data-grid-toggle]');
    const toggleMobile  = document.querySelector('[data-grid-toggle-mobile]');

    if (toggleDesktop) {
      const saved = localStorage.getItem('wc-grid-cols') || '4';
      this._applyGrid(saved, false);
      toggleDesktop.addEventListener('click', () => {
        const current = localStorage.getItem('wc-grid-cols') || '4';
        this._applyGrid(current === '4' ? '3' : '4');
      });
    }

    if (toggleMobile) {
      const saved = localStorage.getItem('wc-grid-cols-mobile') || '2';
      this._applyGridMobile(saved, false);
      toggleMobile.addEventListener('click', () => {
        const current = localStorage.getItem('wc-grid-cols-mobile') || '2';
        this._applyGridMobile(current === '2' ? '1' : '2');
      });
    }
  }

  _applyGrid(cols, save = true) {
    const prev = cols === '4' ? '3' : '4';
    this._grid.classList.remove(`md:grid-cols-${prev}`);
    this._grid.classList.add(`md:grid-cols-${cols}`);
    if (save) localStorage.setItem('wc-grid-cols', cols);
    document.querySelector('.grid-icon-3')?.classList.toggle('hidden', cols === '3');
    document.querySelector('.grid-icon-4')?.classList.toggle('hidden', cols === '4');
  }

  _applyGridMobile(cols, save = true) {
    const prev = cols === '2' ? '1' : '2';
    this._grid.classList.remove(`grid-cols-${prev}`);
    this._grid.classList.add(`grid-cols-${cols}`);
    if (save) localStorage.setItem('wc-grid-cols-mobile', cols);
    document.querySelector('.grid-icon-mobile-1')?.classList.toggle('hidden', cols === '1');
    document.querySelector('.grid-icon-mobile-2')?.classList.toggle('hidden', cols === '2');
  }

  _initFilters() {
    // Tags aplicam imediatamente; preço continua dependendo do botão "Aplicar".
    document.addEventListener('click', this._onFilterClick);
    document.addEventListener('change', this._onFilterChange);
  }

  _onFilterChange(e) {
    const checkbox = e.target.closest('#CollectionFiltersForm .collection-filter-checkbox');
    if (!checkbox || checkbox.disabled) return;

    this._applyFilters();
  }

  _onFilterClick(e) {
    const removeBtn = e.target.closest('[data-filter-remove]');
    if (removeBtn) { e.preventDefault(); this._applyFilters(removeBtn.href); return; }

    const clearAll = e.target.closest('[data-filter-clear-all]');
    if (clearAll) { e.preventDefault(); this._applyFilters(clearAll.href); return; }

    const priceSubmit = e.target.closest('[data-filter-price-submit]');
    if (priceSubmit) { e.preventDefault(); this._applyFilters(); return; }
  }

  async _applyFilters(customUrl) {
    const form = document.querySelector('#CollectionFiltersForm');
    const url  = customUrl || `${window.location.pathname}?${new URLSearchParams(new FormData(form)).toString()}`;
    const openFilterKeys = form
      ? [...form.querySelectorAll('[data-filter-accordion][data-open="true"]')]
          .map(accordion => accordion.dataset.filterKey)
          .filter(Boolean)
      : [];

    this._filterRequestController?.abort();
    this._filterRequestController = new AbortController();

    try {
      const res = await fetch(url, { signal: this._filterRequestController.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const text = await res.text();
      const doc  = new DOMParser().parseFromString(text, 'text/html');

      this._replaceInner('#CollectionFiltersForm',                      doc);
      this._replaceInner('[data-filter-count]',                         doc);
      this._replaceInner('[data-collection-grid]',                      doc);
      this._replaceInner('[data-collection-pagination]',                doc, { all: true });
      this._replaceInner('.filter-drawer__active-facets-wrapper',       doc);
      this._restoreOpenFilters(openFilterKeys);

      history.pushState({ path: url }, '', url);
      this._updateCount();
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('[CollectionManager] Filter failed:', err);
    }
  }

  _restoreOpenFilters(filterKeys) {
    const form = document.querySelector('#CollectionFiltersForm');
    if (!form || !filterKeys.length) return;

    form.querySelectorAll('[data-filter-accordion]').forEach(accordion => {
      if (!filterKeys.includes(accordion.dataset.filterKey)) return;

      const toggle = accordion.querySelector('[data-filter-accordion-toggle]');
      const content = accordion.querySelector('.collection-filter-accordion-content');
      if (!toggle || !content) return;

      accordion.dataset.open = 'true';
      toggle.setAttribute('aria-expanded', 'true');
      content.hidden = false;
    });
  }

  _replaceInner(selector, doc, options = {}) {
    const newEl = doc.querySelector(selector);

    if (!newEl && selector === '[data-collection-pagination]') {
      document.querySelectorAll(selector).forEach(el => {
        el.innerHTML = '';
        el.hidden = true;
      });
      return;
    }

    if (!newEl) return;

    const targets = options.all
      ? document.querySelectorAll(selector)
      : [document.querySelector(selector)].filter(Boolean);

    targets.forEach(el => {
      el.innerHTML = newEl.innerHTML;
      if (selector === '[data-collection-pagination]') {
        el.hidden = newEl.hasAttribute('hidden');
      }
    });
  }

  _initInfiniteScroll() {
    window.addEventListener('scroll', this._onScroll, { passive: true });
  }

  _onScroll() {
    if (!this._firstScroll) { this._firstScroll = true; return; }
    if (this._isLoading) return;

    const threshold = document.body.scrollHeight - window.innerHeight - 200;
    if (window.scrollY >= threshold) this._loadNextPage();
  }

  async _loadNextPage() {
    const nextLink = this._pagination?.querySelector('.next a, a.next');
    if (!nextLink) return;

    this._isLoading = true;
    this._setInfiniteScrollLoading(true);

    try {
      const res = await fetch(nextLink.href);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const text   = await res.text();
      const doc    = new DOMParser().parseFromString(text, 'text/html');
      const newGrid = doc.querySelector('[data-collection-grid]');

      if (newGrid) {
        [...newGrid.children].forEach(child => this._grid.appendChild(child));
      }

      const newPagination = doc.querySelector('[data-collection-pagination]');
      if (this._pagination && newPagination) {
        this._pagination.innerHTML = newPagination.innerHTML;
      }

      this._updateCount();
    } catch (err) {
      console.error('[CollectionManager] Infinite scroll failed:', err);
    } finally {
      this._isLoading = false;
      this._setInfiniteScrollLoading(false);
    }
  }

  _setInfiniteScrollLoading(show) {
    const el = document.querySelector('[data-infinite-scroll-loading]');
    if (!el) return;
    show ? el.removeAttribute('hidden') : el.setAttribute('hidden', '');
  }

  _updateCount() {
    const count   = this._grid?.querySelectorAll('.productCard').length;
    const display = document.querySelector('[data-product-count]');
    if (display && count !== undefined) display.textContent = count;
  }

  destroy() {
    window.removeEventListener('scroll', this._onScroll);
    document.removeEventListener('click', this._onFilterClick);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.CollectionManager = new CollectionManager();
});
